import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import OsScroll from '../ui/OsScroll';
import ContextMenu from '../ui/ContextMenu';
import { CalendarPlus, ArrowRight } from 'lucide-react';
import EventChip from './EventChip';
import {
  monthGridDays,
  isSameDay,
  isSameMonth,
  eventTouchesDay,
  eventRange,
  toDateKey,
  formatDayLong,
  weekdayLabels,
} from '../../utils/calendarDates';

/** Chips shown per cell before collapsing into "+N more" (Google shows 2–4
 *  depending on row height; a fixed 3 keeps the 6-row grid from scrolling). */
const MAX_CHIPS = 3;

/**
 * Month view: fixed 6×7 grid (calendarDates.monthGridDays), event chips with
 * a +N overflow popover, day-number click → Day view, double-click on a cell
 * → quick create for that day. role="grid"/gridcell + aria-selected carry the
 * keyboard-focused day (arrow keys live in CalendarView).
 */
export default function MonthView({
  focusedDate,
  events,
  timeFormat,
  now = new Date(),
  onSelectEvent,
  onEventContextMenu,
  onSelectDay,
  onOpenDay,
  onQuickCreate,
}) {
  const { t, i18n } = useTranslation();
  // Weekday headers come from Intl at the APP locale, not a hardcoded English
  // array — fewer keys and automatically right (de-DE: Mo Di Mi Do Fr Sa So).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekdays = useMemo(() => weekdayLabels('short'), [i18n.language]);
  const [overflow, setOverflow] = useState(null); // { day, rect }
  const [cellMenu, setCellMenu] = useState(null); // { day, x, y }

  const days = useMemo(() => monthGridDays(focusedDate), [focusedDate]);
  const eventsByDay = useMemo(() => {
    return days.map((day) =>
      events
        .filter((e) => eventTouchesDay(e, day))
        .sort((a, b) => {
          if (a.all_day !== b.all_day) return a.all_day ? -1 : 1; // all-day first
          return eventRange(a).start - eventRange(b).start;
        })
    );
  }, [days, events]);

  const closeOverflow = () => setOverflow(null);

  return (
    <div className="cal-month" role="grid" aria-label={t('calendar.view.month')}>
      <div className="cal-month-weekdays" role="row">
        {weekdays.map((label) => (
          <div key={label} className="cal-month-weekday" role="columnheader">
            {label}
          </div>
        ))}
      </div>
      <div className="cal-month-grid">
        {days.map((day, i) => {
          const dayEvents = eventsByDay[i];
          const hiddenCount = Math.max(0, dayEvents.length - MAX_CHIPS);
          const outside = !isSameMonth(day, focusedDate);
          const today = isSameDay(day, now);
          const focused = isSameDay(day, focusedDate);
          return (
            <div
              key={toDateKey(day)}
              role="gridcell"
              aria-selected={focused}
              aria-label={formatDayLong(day)}
              className={`cal-month-cell${outside ? ' cal-month-cell--outside' : ''}`}
              // Single click SELECTS the day (Outlook/Google; fix round 2):
              // focused date moves, aria-selected + the highlight follow, and
              // the keyboard arrows / N / quick-create continue from here.
              // Chips, +N and the day number stopPropagation and keep their
              // own actions. Selecting an adjacent-month day re-anchors the
              // grid to that month — Google's behaviour, shipped as such.
              onClick={() => onSelectDay?.(day)}
              onDoubleClick={() => onQuickCreate?.(day)}
              // Right-click on the DATE card (owner, fix round 4): the shared
              // ContextMenu with New event / Open day. Also selects the day so
              // the menu visibly belongs to it. preventDefault is scoped to
              // the grid cells only — chips stopPropagation before this fires.
              onContextMenu={(e) => {
                e.preventDefault();
                onSelectDay?.(day);
                setCellMenu({ day, x: e.clientX, y: e.clientY });
              }}
            >
              <button
                type="button"
                className={`cal-month-daynum${today ? ' cal-month-daynum--today' : ''}`}
                aria-label={t('calendar.month.openDay', { day: formatDayLong(day) })}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenDay?.(day);
                }}
              >
                {day.getDate()}
              </button>
              {dayEvents.slice(0, MAX_CHIPS).map((event) => (
                <EventChip
                  key={event.uid}
                  event={event}
                  timeFormat={timeFormat}
                  onSelect={onSelectEvent}
                  onEventContextMenu={onEventContextMenu}
                />
              ))}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  className="cal-month-more"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOverflow({ day, dayEvents, rect: e.currentTarget.getBoundingClientRect() });
                  }}
                >
                  {t('calendar.month.moreN', { count: hiddenCount })}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {overflow &&
        ReactDOM.createPortal(
          <>
            {/* click-away layer under the popover */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 999 }}
              onClick={closeOverflow}
              aria-hidden="true"
            />
            <div
              className="cal-popover"
              role="dialog"
              aria-label={t('calendar.month.eventsOn', { day: formatDayLong(overflow.day) })}
              style={{
                top: Math.min(overflow.rect.bottom + 4, (window.innerHeight || 800) - 320),
                left: Math.min(overflow.rect.left, (window.innerWidth || 1200) - 316),
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') closeOverflow();
              }}
            >
              <p className="cal-popover-title">{formatDayLong(overflow.day)}</p>
              {/* OsScroll, not raw overflow — macOS overlay scrollbars hijack
                  hand-rolled scroll regions (gotcha; fix round 4 sweep). */}
              <OsScroll style={{ maxHeight: 260 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {overflow.dayEvents.map((event) => (
                    <EventChip
                      key={event.uid}
                      event={event}
                      timeFormat={timeFormat}
                      onSelect={(ev, rect) => {
                        closeOverflow();
                        onSelectEvent?.(ev, rect);
                      }}
                      onEventContextMenu={(ev, pos) => {
                        closeOverflow();
                        onEventContextMenu?.(ev, pos);
                      }}
                    />
                  ))}
                </div>
              </OsScroll>
            </div>
          </>,
          document.body
        )}

      {cellMenu && (
        <ContextMenu
          x={cellMenu.x}
          y={cellMenu.y}
          items={[
            {
              icon: <CalendarPlus size={13} />,
              label: t('calendar.newEvent'),
              onClick: () => onQuickCreate?.(cellMenu.day),
            },
            {
              icon: <ArrowRight size={13} />,
              label: t('calendar.month.openDayAction'),
              onClick: () => onOpenDay?.(cellMenu.day),
            },
          ]}
          onClose={() => setCellMenu(null)}
        />
      )}
    </div>
  );
}
